package components.project

import components.sales.ClosureCountRequest
import components.sales.ClosureHomepage
import components.sales.ConstructionCountRequest
import components.sales.ConstructionHomepage
import components.sales.PlanningCountRequest
import components.sales.PlanningHomepage
import components.sales.SalesCountRequest
import components.sales.SalesHomepage
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.company.user.AccessRights.AccessRight.ViewProjectCount
import it.neckar.lizergy.model.project.previews.ProjectQueryComponent
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.process.state.LizergyProcessStates
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.react.common.*
import it.neckar.react.common.router.*
import it.neckar.react.common.table.*
import kotlinx.css.*
import kotlinx.html.BUTTON
import kotlinx.html.ButtonType
import kotlinx.html.classes
import kotlinx.html.js.onClickFunction
import react.*
import react.dom.*
import router.useDocumentTitle
import services.UiActions
import services.http.ProjectCountForPhase
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver
import styled.*

/**
 * Loads the company code and shows the projects for this company
 */
val HomepageFromUrl: FC<Props> = fc("HomepageFromUrl") {
  val loggedInUser = useRequireLoggedInUser()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()

  useDocumentTitle(companyName, "Home")

  val projectCountsForPhases: StateInstance<Map<ProjectQueryComponent?, ProjectCountForPhase>?> = useState(null)


  useEffect(loggedInUser) {
    projectCountsForPhases.setter(null)

    UiActions.countProjectPreviews(
      requestedProjectCountsForPhases = SalesCountRequest + PlanningCountRequest + ConstructionCountRequest + ClosureCountRequest,
      loggedInUser = loggedInUser.loginName,
    ) {
      projectCountsForPhases.setter(it?.data?.associateBy { countForPhase -> countForPhase.projectQueryComponent })
    }
  }


  div {

    div {
      div("row gx-5") {

        div("col-lg-3 col-md-6 col-12") {
          SalesHomepage {
            attrs {
              this.projectCountsForPhases = projectCountsForPhases.value
            }
          }
        }

        div("col-lg-3 col-md-6 col-12") {
          PlanningHomepage {
            attrs {
              this.projectCountsForPhases = projectCountsForPhases.value
            }
          }
        }

        div("col-lg-3 col-md-6 col-12") {
          ConstructionHomepage {
            attrs {
              this.projectCountsForPhases = projectCountsForPhases.value
            }
          }
        }

        div("col-lg-3 col-md-6 col-12") {
          ClosureHomepage {
            attrs {
              this.projectCountsForPhases = projectCountsForPhases.value
            }
          }
        }

      }
    }

    div("my-5") {
      h2 {
        +"Alle Projekte"
      }
      ProjectsOverview {
        attrs {
          this.defaultFilteredBy = null
          this.processStatesToFilter = LizergyProcessStates.allProcessStates
          this.processStatesToHide = listOf(ProjectProcessStates.Archived, ProjectProcessStates.Paused)
          this.projectQueryComponent = null
          this.tableColumns = { ServerProjectPreviewsTable.homepageColumns(filteredProcessState = it, userResolver = userResolver, companyResolver = companyResolver) }
          this.sortedBy = SortedBy.SortedDescending
          this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
          this.includeArchived = true
          this.keyPrefix = LocalStorageKeyPrefix("HomePage")
        }
      }
    }

  }
}

fun RBuilder.homepageButton(url: NavigateUrl, content: RDOMBuilder<BUTTON>.() -> Unit) {
  homepageButton({
    invoke(url)
  }, content)
}

fun RBuilder.homepageButton(navigationAction: NavigationAction, content: RDOMBuilder<BUTTON>.() -> Unit) {
  val navigate = useNavigateUrl()

  styledButton(type = ButtonType.button) {
    attrs {
      classes = setOf("col", "btn", "btn-primary", "btn-lg", "position-relative")
      onClickFunction = {
        navigate.navigationAction()
      }
    }
    css {
      minHeight = 49.px
    }

    content(this)
  }
}

fun RBuilder.homepageButtonForPhase(url: NavigateUrl, title: String, projectCountForPhase: ProjectCountForPhase?) {
  HomepageButton {
    attrs {
      this.url = url
      this.title = title
      this.projectCountForPhase = projectCountForPhase
    }
  }
}

val HomepageButton: FC<HomepageButtonProps> = fc("HomepageButton") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val url = props::url.safeGet()
  val title = props::title.safeGet()
  val projectCountForPhase = props::projectCountForPhase.safeGet()

  homepageButton(url) {
    if (projectCountForPhase?.projectCountForUser != null && projectCountForPhase.projectCountForUser > 0) {
      span("position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger") {
        +"${projectCountForPhase.projectCountForUser}"
        span("visually-hidden") { +"Projekte für ${loggedInUser.editorName}" }
      }
    }

    b { +title }
    if (loggedInUser.accessRights.canAccess(ViewProjectCount)) {
      span("fs-6 fw-lighter ps-2") {
        busyIfNull(projectCountForPhase?.totalProjectCount, small = true) { +"($it)" }
      }
    }
  }
}

external interface HomepageButtonProps : Props {
  var url: NavigateUrl
  var title: String
  var projectCountForPhase: ProjectCountForPhase?
}

external interface HomepageProps : Props {
  var projectCountsForPhases: Map<ProjectQueryComponent?, ProjectCountForPhase?>?
}

